/**
 * Resizes an image file to fit within the specified maximum dimensions while maintaining aspect ratio.
 * If the image is already within the limits, returns the original file.
 * @param file - The image file to resize
 * @param maxWidth - Maximum width in pixels
 * @param maxHeight - Maximum height in pixels
 * @returns Promise that resolves to the resized File (or original if no resize needed)
 */
export const resizeImage = (
  file: File,
  maxWidth: number,
  maxHeight: number
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let newWidth = img.width;
      let newHeight = img.height;

      if (img.width > maxWidth || img.height > maxHeight) {
        const widthRatio = maxWidth / img.width;
        const heightRatio = maxHeight / img.height;
        const ratio = Math.min(widthRatio, heightRatio);

        newWidth = Math.round(img.width * ratio);
        newHeight = Math.round(img.height * ratio);
      }

      // If dimensions are within limits, return original file
      if (newWidth === img.width && newHeight === img.height) {
        resolve(file);
        return;
      }

      // Resize the image
      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject("Canvas context not available");
        return;
      }

      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
            });
            resolve(resizedFile);
          } else {
            reject("Failed to resize image");
          }
        },
        file.type || "image/png"
      );
    };

    img.onerror = () => {
      reject("Error loading image");
    };

    reader.readAsDataURL(file);
  });
};

